"use client";
import { signOut, useSession, signIn } from "next-auth/react";
import Image from "next/image";
import { Bell, ChevronDown, Cloud, Menu, Settings } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import { useSidebar } from "./SidebarContext";

// Top navigation bar component
const Navbar = (props) => {
  const { data, status } = useSession();
  const Session = data;
  const userdata = Session?.user;
  const User = { username: userdata?.name, email: userdata?.email };
  const router = useRouter();
  const { toggleSidebar } = useSidebar();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const { sticky } = props;

  // Handle logout functionality
  const handleLogout = async () => {
    try {
      console.log('Logout initiated...');
      
      // 1. Call NextAuth signout with proper options
      await signOut({ 
        redirect: false,
        callbackUrl: '/auth/signin' 
      });
      
      // 2. Clear all cookies manually (including domain variations)
      if (typeof document !== 'undefined') {
        const cookies = document.cookie.split(';');
        const hostname = window.location.hostname;
        
        for (let cookie of cookies) {
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          
          // Clear with different path and domain combinations
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${hostname}`;
          
          // Also try with leading dot for subdomains
          if (hostname.includes('.')) {
            const rootDomain = hostname.split('.').slice(-2).join('.');
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${rootDomain}`;
          }
        }
      }
      
      // 3. Clear localStorage and sessionStorage
      if (typeof localStorage !== 'undefined') {
        localStorage.clear();
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }
      
      // 4. Force redirect with window.location (more reliable)
      console.log('Redirecting to signin...');
      window.location.href = '/auth/signin';
      
    } catch (error) {
      console.error('Logout error:', error);
      // Force redirect anyway
      window.location.href = '/auth/signin';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <>
      <nav
        className={`${
          sticky ? "fixed" : ""
        } top-0 z-50 w-full h-[63.2px] bg-white border-b border-[#0000001A] shadow-md transition-shadow`}
      >
        <div className="px-3 py-3 lg:px-8 lg:pl-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center justify-start rtl:justify-end gap-4">
              {/* Mobile menu button - closes sidebar on 768px screens */}
              <button
                data-drawer-target="logo-sidebar"
                data-drawer-toggle="logo-sidebar"
                aria-controls="logo-sidebar"
                type="button"
                aria-label="Open sidebar"
                className="inline-flex items-center p-2 text-sm text-gray-500 rounded-lg md:hidden hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#284F9E]"
                onClick={toggleSidebar}
              >
                <span className="sr-only text-[#284F9E]">Open sidebar</span>
                <Menu size={28} strokeWidth={2.75} color="#284F9E" />
              </button>
              {/* Logo link */}
              <Link
                href="/dashboard"
                className="w-fit flex items-center ms-2 md:me-24 group"
              >
                <Image
                  src="/images/icons/PIXCON-LOGO.png"
                  className="me-3 -mt-2 w-auto h-10 group-hover:scale-105 transition-transform"
                  height={40}
                  width={120}
                  style={{ width: "auto" }}
                  alt="Pixcon"
                  priority
                />
              </Link>

              {/* Search form - hidden on mobile */}
              {/* <form className="md:block hidden items-center w-[480px] mr-auto">
                <label htmlFor="simple-search" className="sr-only">
                  Search
                </label>
                <div className="relative w-full">
                  <div className="absolute inset-y-0 start-0 flex items-center ps-3">
                    <svg
                      className="w-5 h-5 text-[#667185] hover:cursor-pointer"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeWidth="2"
                        d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="simple-search"
                    className="bg-white w-full outline-none border border-[#E4E4E4] text-[#667185] text-sm rounded-lg block ps-10 p-2 focus:ring-2 focus:ring-[#284F9E] focus:border-[#284F9E] transition-shadow"
                    placeholder="Search here"
                    required
                    aria-label="Search here"
                  />
                </div>
              </form> */}
            </div>
            {/* Right side navigation items */}
            <div className="flex items-center gap-4">
              <div className="flex items-center ms-2 gap-2">
                {/* Cloud button */}
                <button
                  type="button"
                  className="flex text-sm rounded-full hover:bg-gray-100 p-2 focus:outline-none focus:ring-2 focus:ring-[#284F9E]"
                  aria-label="Cloud"
                >
                  <Cloud color="#284F9E" />
                </button>

                <div className="border-l-2 h-5 border-[#E4E4E4] mx-1"></div>

                {/* Notifications button */}
                <button
                  type="button"
                  className="flex text-sm rounded-full hover:bg-gray-100 p-2 focus:outline-none focus:ring-2 focus:ring-[#284F9E]"
                  aria-label="Notifications"
                >
                  <Bell color="#284F9E" />
                </button>

                <div className="border-l-2 h-5 border-[#E4E4E4] mx-1"></div>
                
                {/* User profile dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    className="flex text-sm items-center gap-2 rounded-full hover:bg-gray-100 p-1 focus:outline-none focus:ring-2 focus:ring-[#284F9E]"
                    aria-expanded={isDropdownOpen}
                    aria-label="Open user menu"
                    onClick={toggleDropdown}
                  >
                    <span className="sr-only">Open user menu</span>
                    <Image
                      className="w-8 h-8 rounded-full object-contain border border-[#E4E4E4] bg-gray-100"
                      width={32}
                      height={32}
                      src={
                        userdata?.image ||
                        "/images/icons/user-image-with-black-background.png"
                      }
                      alt="user photo"
                      priority
                    />
                    <p className="text-sm capitalize lg:block hidden font-semibold text-[#212121] text-nowrap">
                      {User?.username || ""}
                    </p>
                    <ChevronDown color="#667185" className={`lg:block hidden transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {/* User dropdown menu */}
                  <div
                    className={`z-50 my-0 text-base list-none bg-white text-black divide-y divide-[#58bae9] rounded shadow-lg min-w-[180px] absolute right-0 top-12 transition-all duration-200 ${
                      isDropdownOpen
                        ? 'opacity-100 visible transform translate-y-0'
                        : 'opacity-0 invisible transform -translate-y-2 pointer-events-none'
                    }`}
                  >
                    <div className="px-4 py-2" role="none">
                      <p className="text-sm capitalize text-gray-900 " role="none">
                        {User?.username || ""}
                      </p>
                      <p
                        className="text-sm font-medium text-gray-900 truncate dark:text-gray-300"
                        role="none"
                      >
                        {User?.email}
                      </p>
                    </div>
                    <ul className="py-1" role="none">
                      <li>
                        <a
                          onClick={handleLogout}
                          className="block px-4 hover:cursor-pointer py-2 text-sm text-black hover:bg-[#ECF2FF] transition-colors"
                          role="menuitem"
                        >
                          Sign out
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;